import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Fade,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  createTheme,
  ThemeProvider,
  alpha,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ReactMarkdown from "react-markdown";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

import { getOrFetchToken } from "./utils/token";
import { API_BASE_URL } from "./config";
import { loadUsageCount, incrementUsageCount } from "./utils/usageLimit";
import EyesLoader from "./components/EyesLoader";
import "./styles.css";

const MAX_USES_PER_DAY = 5;
const MAX_SIZE_BYTES = 30 * 1024 * 1024; // 30MB

export default function App() {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [pageInput, setPageInput] = useState("");
  const [bookTitle, setBookTitle] = useState("");
  const [bookAuthor, setBookAuthor] = useState("");
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usageCount, setUsageCount] = useState(0);
  const [darkMode, setDarkMode] = useState(true);
  const [demoMode, setDemoMode] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("warning"); // "success" | "error" | "info" | "warning"

  const showAlert = (message, severity = "warning") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };



  const DEMO_SUMMARY = `\`\`\`text
┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓
┃                      The Hitchhiker’s Guide to the Galaxy (Up to             ┃
┃                                   Page 42)                                   ┃
┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛
────────────────────────────────────────────────────────────────────────────────
                         Arthur Dent’s Terrible Morning
Arthur Dent wakes up to find his quiet life thrown into chaos. His beloved home
is about to be demolished to make way for a new bypass. Panicked, Arthur tries
to stop the process by lying in front of the bulldozers. Mr. Prosser, the
nervous council worker overseeing everything, just wants to finish his job and
go home.
────────────────────────────────────────────────────────────────────────────────
                     Ford Prefect Arrives With Urgent News
Suddenly, Ford Prefect, Arthur’s strange friend, shows up acting more bizarre
than usual. He insists Arthur must come to the pub immediately, hinting that
something much bigger is about to happen. Ford convinces Mr. Prosser to take
Arthur’s place in the mud outside the house, allowing Arthur to leave without
losing his protest spot. The two friends head to the local pub, Arthur confused
and upset.
────────────────────────────────────────────────────────────────────────────────
                    The End of the World Revealed at the Pub
Inside the pub, Ford struggles to break shocking news to Arthur: the world is
ending. While Ford drinks and gathers peanuts (which he claims are important),
Arthur fixates on his house, but learns it’s already being torn down. Abruptly,
disaster escalates—a fleet of huge yellow spaceships appears above Earth. The
Vogons announce by universal broadcast that the entire planet will be destroyed
for a galactic construction project. Arthur’s personal crisis is suddenly
irrelevant compared to the planet’s fate.
────────────────────────────────────────────────────────────────────────────────
                     Zoom Out: Life Elsewhere in the Galaxy
The narrative shifts far from Earth to the planet Damogran. Zaphod Beeblebrox,
the wild, two-headed President of the Galaxy, arrives surrounded by reporters
and security. With him is Trillian, a mysterious woman who once knew life on
Earth. Excitement builds as crowds gather to witness the unveiling of the
powerful new starship, the Heart of Gold.
────────────────────────────────────────────────────────────────────────────────
                          Big Galaxy, Bigger Problems
Zaphod relishes the attention, cracking jokes and flirting with the idea of
stealing the Heart of Gold for himself. The ship’s reveal is dramatic, marked by
pageantry and spectacle. Meanwhile, back on Earth, the local and galactic
bureaucracies mirror each other—both casually wiping out lives for a “greater
good.” The story hints at a wild, unpredictable universe, far stranger than
Arthur ever imagined.
────────────────────────────────────────────────────────────────────────────────
                           Key Anchors and Takeaways
 • Arthur’s house being demolished = Earth’s looming destruction, both for
   bureaucratic reasons.
 • Ford Prefect is not from Earth and knows more than he says; trust the friend
   who’s acting weird.
 • Zaphod Beeblebrox and Trillian introduce new faces and rules: life is bigger,
   messier, and funnier than it seemed.
 • The galaxy is full of chaos, danger, and bizarre characters—a huge leap from
   Arthur’s rural England.
────────────────────────────────────────────────────────────────────────────────
                           Recap of Critical Details
 • Arthur Dent: Caught up in disaster, bewildered by it all.
 • Ford Prefect: Secret alien, holds key knowledge.
 • Mr. Prosser: Bureaucrat, minor player.
 • Vogons: Announce Earth’s imminent doom.
 • Zaphod Beeblebrox & Trillian: Bring new energy and possibility—the world as
   Arthur knows it is over.
 • Heart of Gold: Mysterious ship, symbol of the unknown galaxy.
────────────────────────────────────────────────────────────────────────────────
Transition:
From an ordinary English morning, Arthur’s world flips upside down—first by
bulldozers, then by alien ships. The story expands from a backyard crisis to a
universal one, setting up new adventures across space.
────────────────────────────────────────────────────────────────────────────────
Remember:
Earth is about to be destroyed, and nothing will be normal again. The next pages
promise even stranger discoveries and wild new journeys.
Stop here to avoid spoilers past page 42!
\`\`\``;


  useEffect(() => setUsageCount(loadUsageCount()), []);

  // expose light-mode class for CSS background variants
  useEffect(() => {
    const el = document.documentElement;
    if (darkMode) el.classList.remove("light");
    else el.classList.add("light");
  }, [darkMode]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: { main: "#6C63FF" },
          secondary: { main: "#00D1FF" },
          ...(darkMode
            ? { background: { default: "#0B0D13", paper: alpha("#121524", 0.5) } }
            : {
                background: { default: "#F6F8FF", paper: "#FFFFFF" },
                text: { primary: "#0F1222", secondary: alpha("#0F1222", 0.68) },
                divider: alpha("#0F1222", 0.12),
              }),
        },
        typography: {
          
           fontFamily:
         '"Courier New", Courier, "Liberation Mono", "Nimbus Mono L", Consolas, Menlo, Monaco, monospace',
          h3: { fontWeight: 800, letterSpacing: -0.5 },
          h5: { fontWeight: 700 },
          button: { fontWeight: 700, textTransform: "none", letterSpacing: 0.2 },
        },
        shape: { borderRadius: 18 },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backdropFilter: darkMode ? "blur(14px)" : "none",
                boxShadow: darkMode
                  ? "0 20px 60px rgba(0,0,0,.45)"
                  : "0 10px 40px rgba(15,18,34,.08)",
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: { borderRadius: 14, paddingBlock: 12 },
              containedPrimary: {
                boxShadow: darkMode
                  ? "0 10px 30px rgba(108,99,255,.35)"
                  : "0 8px 22px rgba(108,99,255,.25)",
              },
              outlined: {
                borderColor: darkMode
                  ? alpha("#FFFFFF", 0.18)
                  : alpha("#0F1222", 0.18),
                "&:hover": {
                  borderColor: darkMode
                    ? alpha("#FFFFFF", 0.32)
                    : alpha("#0F1222", 0.32),
                },
              },
            },
          },
        },
      }),
    [darkMode]
  );

const onDrop = (acceptedFiles) => {
  const uploadedFile = acceptedFiles[0];
  if (!uploadedFile) return;

  setFile(uploadedFile);
  setFileName(uploadedFile.name);

  // leave demo mode and reset demo content/inputs
  setDemoMode(false);
  setSummary("");        // remove the demo summary
  setPageInput("");      // show placeholders again
  setBookTitle("");
  setBookAuthor("");
  setError(null);
};




  const inputId = "drop-input";

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: (rejections) => {
      const tooBig = rejections.some((r) =>
        r.errors.some((e) => e.code === "file-too-large")
      );
      if (tooBig) setError("File is larger than 30MB.");
    },
    multiple: false,
    maxSize: MAX_SIZE_BYTES,
    accept: {
      "application/pdf": [".pdf"],
      "application/epub+zip": [".epub"],
    },
  });

  const handleSubmit = async () => {
  if (usageCount >= MAX_USES_PER_DAY)
    return showAlert("Daily limit reached.", "warning");

  if (demoMode)
    return showAlert(
      "This is just a demo example — please upload your own file to use Refresh memory.",
      "info"
    );

  if (!file || !pageInput)
    return showAlert("Upload a file and set the current page first.", "warning");

  setLoading(true);
  setError(null);
  setSummary("");

  try {
    const token = await getOrFetchToken();
    const formData = new FormData();
    formData.append("file", file);
    formData.append("current_page", pageInput);
    formData.append("title", bookTitle);
    formData.append("author", bookAuthor);

    const response = await fetch(`${API_BASE_URL}/api/summarize`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      let message = "Request failed";
      try {
        const j = await response.json();
        message = j.detail || j.message || message;
      } catch {}
      throw new Error(message);
    }

    const data = await response.json();
    setSummary(data.summary || "");
    setUsageCount(incrementUsageCount(usageCount));
  } catch (err) {
    setError(err.message || "Unexpected error");
  } finally {
    setLoading(false);
  }
};



const handleDemoBook = () => {
  setBookTitle("The Hitchhiker’s Guide to the Galaxy");
  setBookAuthor("Douglas Adams");
  setPageInput("42");
  setSummary(DEMO_SUMMARY);
  setFile(null);
  setFileName("");
  setDemoMode(true);
};

  return (
    <ThemeProvider theme={theme}>
      <Snackbar
  open={alertOpen}
  autoHideDuration={4000}
  onClose={() => setAlertOpen(false)}
  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
>
  <MuiAlert
    elevation={6}
    variant="filled"
    severity={alertSeverity}
    onClose={() => setAlertOpen(false)}
    sx={{
      borderRadius: 3,
      fontWeight: 600,
      bgcolor:
        alertSeverity === "info"
          ? (theme) =>
              theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.main, 0.8)
                : theme.palette.primary.main
          : undefined,
    }}
  >
    {alertMessage}
  </MuiAlert>
</Snackbar>
      <CssBaseline />

      {/* background */}
      <Box className="bg-wrap">
        <div className="blob blob-1" />
        <div className="blob blob-2" />
        <div className="stars" />
      </Box>

      {/* top bar */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, pt: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {/* brand */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              component="img"
              className="brand-logo"
              // src={darkMode ? "https://storage.googleapis.com/pagesage-frontend-react/page-sage-logo-dark.png" : "https://storage.googleapis.com/pagesage-frontend-react/page-sage-logo-light.png"}
              src={darkMode ? "/page-sage-logo-dark.png" : "/page-sage-logo-light.png"}

              alt="PageSage"
              sx={{
                height: { xs: 72, sm: 90, md: 110 },
                width: "auto",
                userSelect: "none",
                transition: "transform .18s ease, filter .25s ease",
                filter: darkMode
                  ? "drop-shadow(0 10px 28px rgba(108,99,255,.45))"
                  : "drop-shadow(0 12px 28px rgba(15,18,34,.15))",
                "&:hover": { transform: "translateY(-2px)" },
              }}
            />
          </Box>

          {/* dark toggle */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Dark Mode
            </Typography> */}
            {/* <Switch checked={darkMode} onChange={() => setDarkMode(!darkMode)} /> */}
            <Tooltip title={darkMode ? "Switch to light" : "Switch to dark"}>
              <IconButton color="primary" onClick={() => setDarkMode(!darkMode)}>
                {darkMode ? <DarkModeRoundedIcon /> : <LightModeRoundedIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Container>

      {/* hero */}
      <Container maxWidth="md" sx={{ position: "relative", zIndex: 2, py: { xs: 4, md: 8 } }}>
        <Stack spacing={2} alignItems="center" textAlign="center" mb={2}>
          <Typography
            component="h1"
            className="hero-headline"
            sx={{
              fontFamily: '"Bitcount", system-ui, -apple-system, "Segoe UI", Arial, sans-serif',
              fontSize: { xs: "28px", sm: "34px", md: "42px" },
              lineHeight: 1.15,
              letterSpacing: "0.015em",
              textAlign: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            Remember what the hell
            <br />
            was happening
            <br />
            in that book
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: 700,
              opacity: darkMode ? 0.85 : 0.9,
              color: "text.secondary",
            }}
          >
            Are you a book abandoner? This is a safe space. Upload your book and
            get a spoiler-free summary of what’s happened so far.
          </Typography>
        </Stack>

        {/* form wrapper */}
        <Box
          sx={{
            p: 0,
            mt: 1,
            position: "relative",
            background: "transparent",
            boxShadow: "none",
            border: "none",
            borderRadius: 0,
            maxWidth: 620,
            width: "100%",
            mx: "auto",
          }}
        >
          {/* Dropzone */}
          <Box
            {...getRootProps()}
            className="dropzone"
            sx={{
              position: "relative",
              p: { xs: 3, md: 3.5 },
              mb: 3,
              borderRadius: 6,
              border: `2px dashed ${
                isDragActive
                  ? theme.palette.primary.main
                  : alpha(theme.palette.text.primary, 0.35)
              }`,
              bgcolor: darkMode ? "rgba(0,0,0,.16)" : alpha("#0F1222", 0.02),
              transition: "all .2s ease",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                bgcolor: darkMode ? "rgba(0,0,0,.22)" : alpha("#0F1222", 0.04),
              },
              textAlign: "center",
              cursor: "pointer",
            }}
          >
            {/* single hidden input for dropzone */}
            <input
              {...getInputProps({
                id: inputId,
                onClick: (e) => {
                  // allow re-selecting the same file (even if it has same name)
                  e.target.value = null;
                },
              })}
              hidden
            />

            {/* info bubble */}
            <Tooltip
              arrow
              placement="top"
              title={
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.25 }}>
                    Supported files
                  </Typography>
                  <Typography variant="body2">.pdf or .epub</Typography>
                  <Typography variant="body2">Max size 30MB</Typography>
                </Box>
              }
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "rgba(0,0,0,.92)",
                    "& .MuiTooltip-arrow": { color: "rgba(0,0,0,.92)" },
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{ position: "absolute", top: 10, right: 10, opacity: 0.9 }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    lineHeight: "22px",
                    textAlign: "center",
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#fff",
                    background: "linear-gradient(180deg,#756DFF 0%,#6C63FF 100%)",
                  }}
                >
                  i
                </span>
              </IconButton>
            </Tooltip>

            <Stack spacing={1.5} alignItems="center" justifyContent="center">
              <CloudUploadRoundedIcon
                sx={{ fontSize: 34, color: theme.palette.primary.main, mb: 0.5, opacity: 0.95 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {fileName ? "File selected" : "Drop a book here"}
              </Typography>

              {/* choose file — points to the hidden input above */}
              <Button
                component="label"
                htmlFor={inputId}
                variant={darkMode ? "outlined" : "contained"}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  py: 1,
                  ...(darkMode
                    ? {}
                    : { background: "linear-gradient(180deg,#756DFF 0%,#6C63FF 100%)" }),
                }}
                onClick={(e) => e.stopPropagation()}
              >
                Choose file
              </Button>

              {fileName && (
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  <strong>{fileName}</strong>
                </Typography>
              )}
            </Stack>
          </Box>

          {/* Inputs – labels above, pill look */}
          <Stack spacing={2.25} sx={{ alignItems: "center", textAlign: "center" }}>
            <Box sx={{ width: "100%" }}>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Last page read?
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <input
                  className="ps-input"
                  placeholder="42"
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  inputMode="numeric"
                  style={{
                    height: 44,
                    maxWidth: 120,
                    textAlign: "center",
                    borderRadius: 999,
                    background: darkMode ? "rgba(0,0,0,.22)" : "#FFFFFF",
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                    boxShadow: darkMode
                      ? "inset 0 1px 0 rgba(255,255,255,.04)"
                      : "0 1px 0 rgba(15,18,34,.05)",
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ width: "100%" }}>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Title <em>(optional)</em>
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <input
                  className="ps-input"
                  placeholder="The Hitchhiker’s Guide to the Galaxy"
                  value={bookTitle}
                  onChange={(e) => setBookTitle(e.target.value)}
                  style={{
                    height: 46,
                    maxWidth: 560,
                    width: "100%",
                    borderRadius: 999,
                    background: darkMode ? "rgba(0,0,0,.22)" : "#FFFFFF",
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ width: "100%" }}>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Author <em>(optional)</em>
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <input
                  className="ps-input"
                  placeholder="Douglas Adams"
                  value={bookAuthor}
                  onChange={(e) => setBookAuthor(e.target.value)}
                  style={{
                    height: 46,
                    maxWidth: 560,
                    width: "100%",
                    borderRadius: 999,
                    background: darkMode ? "rgba(0,0,0,.22)" : "#FFFFFF",
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                  }}
                />
              </Box>
            </Box>

            {/* buttons */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 0.5, width: "100%", justifyContent: "center" }}
            >
              <Button
                onClick={handleSubmit}
                size="large"
                variant="contained"
                sx={{
                  minWidth: 220,
                  borderRadius: 18,
                  px: 3,
                  ...(darkMode
                    ? {}
                    : { background: "linear-gradient(180deg,#8C6DFF 0%,#6C63FF 100%)" }),
                }}
              >
                {loading ? "Generating…" : "Refresh memory"}
              </Button>

              <Button
                onClick={handleDemoBook}
                size="large"
                variant="outlined"
                sx={{ minWidth: 220, borderRadius: 18, px: 3 }}
              >
                See an example
              </Button>
            </Stack>
          </Stack>

          {/* Eyes loading overlay — keep INSIDE the wrapper */}
          <Fade in={loading} unmountOnExit>
            <Box
              className="loading-overlay"
              sx={{
                borderRadius: 6,
                zIndex: 5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <EyesLoader size={76} />
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Reading your book…
              </Typography>
            </Box>
          </Fade>
        </Box>

        {/* error */}
        {error && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 2, color: "error.main" }}
          >
            <ErrorOutlineRoundedIcon />
            <Typography>{error}</Typography>
          </Stack>
        )}

       {/* results */}
<Fade in={Boolean(summary)} timeout={400} unmountOnExit>
  <Paper
    elevation={10}
    sx={{
      mt: 4,
      p: { xs: 2.5, md: 3 },
      borderRadius: 4,
      border: `1px solid ${darkMode ? alpha("#FFFFFF", 0.06) : alpha("#0F1222", 0.08)}`,
      background: darkMode
        ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.96)}, ${alpha(
            theme.palette.background.paper,
            0.84
          )})`
        : "linear-gradient(180deg, #FFFFFF 0%, #FDFEFF 100%)",
    }}
  >
    {/* Friendly header */}
    <Box
      className="result-header"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        mb: 2,
        px: 1.25,
        py: 0.75,
        borderRadius: 999,
        width: "fit-content",
        background:
          "linear-gradient(180deg, rgba(124,113,255,.16) 0%, rgba(108,99,255,.10) 100%)",
        border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
        boxShadow: darkMode ? "0 6px 24px rgba(0,0,0,.25)" : "0 6px 20px rgba(15,18,34,.08)",
      }}
    >
      <span role="img" aria-label="bookmark" style={{ fontSize: 18 }}>🔖</span>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
        Last time on your reading journey…
      </Typography>
    </Box>

    {/* Eyebrow / subtitle */}
    <Typography
      variant="body2"
      className="eyebrow"
      sx={{ mb: 2, opacity: 0.8 }}
    >
      {pageInput
        ? `No spoilers past page ${pageInput}.`
        : "No spoilers beyond your current page."}
    </Typography>

    {/* The content */}
    <Box className="summary-content">
      <ReactMarkdown
        components={{
          h1: (props) => <Typography variant="h5" fontWeight={800} gutterBottom {...props} />,
          h2: (props) => <Typography variant="h6" fontWeight={800} gutterBottom {...props} />,
          h3: (props) => <Typography variant="subtitle1" fontWeight={800} gutterBottom {...props} />,
          p:  (props) => <Typography sx={{ mb: 1.5, opacity: 0.92 }} {...props} />,
          li: (props) => <li style={{ marginBottom: 8 }} {...props} />,
          strong: (props) => <strong style={{ fontWeight: 750 }}>{props.children}</strong>,
          em: (props) => <em style={{ fontStyle: "italic" }}>{props.children}</em>,
        }}
      >
        {summary}
      </ReactMarkdown>
    </Box>
  </Paper>
</Fade>
{/* Buy Me a Coffee button */}
 <Box sx={{ textAlign: "center", mt: 12 }}>
  <a href="https://www.buymeacoffee.com/wherewasi" target="_blank" rel="noopener noreferrer">
    <img
      src="https://img.buymeacoffee.com/button-api/?text=Buy us a coffee&emoji=☕&slug=wherewasi&button_colour=6c63ff&font_colour=ffffff&font_family=Bree&outline_colour=ffffff&coffee_colour=FFDD00"
      alt="Buy us a coffee"
      style={{ height: 60, width: "auto" }}
    />
  </a>
</Box>

      </Container>

      {/* footer */}
      <Container sx={{ py: 4, textAlign: "center", opacity: 0.6 }}>
        <Typography variant="caption">
          © {new Date().getFullYear()} where was i? - For all those who have lost their place (literally or figuratively)
        </Typography>
      </Container>
    </ThemeProvider>
  );
}
